import React, { createContext, useContext } from "react"
import * as Icons from "@nova/component-icons"
import { Notification as NotificationType } from "@nova/schemas"
import { ButtonTertiary } from "@nova/component-btn"
import { formatFeedTime, formatRelativeTime } from "@nova/format"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { ENTITY_ID_PARAM } from "features/map/useEntityIdDeepLink"
import { useAppSelector } from "integrations/redux/hooks"
import { useRouter } from "pages"
import { useMarkNotificationRead } from "./NotificationQueries"

export type NotificationTarget = { projectId: string; entityId?: string }

const NotificationContext = createContext<{
    notification: NotificationType
    markRead: () => Promise<void>
    navigate: (target: NotificationTarget) => void
} | null>(null)

const useNotification = () => {
    const value = useContext(NotificationContext)
    if (!value) throw new Error("Notification slots must be rendered inside <Notification>")
    return value
}

const GroupedByDayContext = createContext(false)

export const NotificationGroupedByDay = ({ children }: { children: React.ReactNode }) => (
    <GroupedByDayContext.Provider value={true}>{children}</GroupedByDayContext.Provider>
)

const Root = ({
    notification,
    children,
}: {
    notification: NotificationType
    children: React.ReactNode
}) => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const router = useRouter("/")
    const { markNotificationRead } = useMarkNotificationRead()

    const markRead = async () => {
        if (!notification.readAt) await markNotificationRead(notification.id)
    }

    const navigate = ({ projectId, entityId }: NotificationTarget) => {
        router.navigate({
            path: "/workspace/:workspaceId/project/:projectId",
            params: { workspaceId, projectId },
            ...(entityId ? { search: { [ENTITY_ID_PARAM]: entityId } } : {}),
        })
    }

    return (
        <NotificationContext.Provider value={{ notification, markRead, navigate }}>
            <div
                onClick={() => void markRead()}
                className="flex flex-col p-3 rounded-xl transition-colors cursor-pointer hover:bg-white-100"
            >
                <div className="flex gap-3 items-start w-full">{children}</div>
            </div>
        </NotificationContext.Provider>
    )
}

const Icon = ({ children }: { children: React.ReactNode }) => (
    <div className="relative shrink-0 w-8 h-8">{children}</div>
)

const Content = ({ children }: { children: React.ReactNode }) => (
    <div className="flex flex-1 flex-col gap-1 min-w-0">{children}</div>
)

const Heading = ({ children }: { children: React.ReactNode }) => {
    const { notification } = useNotification()
    const groupedByDay = useContext(GroupedByDayContext)

    const timestamp = groupedByDay
        ? formatFeedTime(notification.createdAt)
        : formatRelativeTime(notification.createdAt)

    return (
        <div className="flex gap-2 items-start w-full">
            <div className="flex flex-1 flex-col min-w-0">{children}</div>
            <div className="flex gap-1 items-center h-5 shrink-0">
                <small className="text-gray-300">{timestamp}</small>
                {!notification.readAt && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
            </div>
        </div>
    )
}

const Title = ({ children }: { children: React.ReactNode }) => (
    <div className="text-white leading-5 truncate">{children}</div>
)

const Subtitle = ({ children }: { children: React.ReactNode }) => (
    <div className="text-white leading-5 line-clamp-2">{children}</div>
)

const Body = ({ children }: { children: React.ReactNode }) => (
    <div className="text-gray-100 leading-[1.4] line-clamp-2">{children}</div>
)

const Action = ({
    label,
    RightIcon,
    to,
}: {
    label: string
    RightIcon?: Icons.IconComponent
    to: NotificationTarget
}) => {
    const { markRead, navigate } = useNotification()
    return (
        <div className="pt-1 w-full">
            <ButtonTertiary
                title={label}
                RightIcon={RightIcon}
                className="w-full"
                onClick={(e) => {
                    e.stopPropagation()
                    navigate(to)
                    void markRead()
                }}
            />
        </div>
    )
}

export const Notification = Object.assign(Root, {
    Icon,
    Content,
    Heading,
    Title,
    Subtitle,
    Body,
    Action,
})
